import React, { useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { applyHotelAvailability, applyRoomTypeAvailability, BulkAvailabilityPayload } from '../../api/hotelAvailability.ts';
import { toastError, toastSuccess } from '../../shared/ui/toast.ts';

type Props = { hotelId: number; roomTypes?: { id: number; name: string }[] };

const AvailabilityTab: React.FC<Props> = ({ hotelId, roomTypes = [] }) => {
  const [start, setStart] = useState<Dayjs | null>(dayjs());
  const [end, setEnd] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [allotment, setAllotment] = useState<string>('');
  const [closed, setClosed] = useState<boolean>(false);
  const [price, setPrice] = useState<string>('');
  const [selRoomTypeId, setSelRoomTypeId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const payload = useMemo<BulkAvailabilityPayload | null>(() => {
    if (!start || !end) return null;
    const s = start.format('YYYY-MM-DD');
    const e = end.format('YYYY-MM-DD');
    const p: BulkAvailabilityPayload = { start: s, end: e } as any;
    if (allotment !== '') p.allotment = Number(allotment);
    if (price !== '') p.priceOverride = Number(price);
    if (closed) p.closed = true; // only send when true to avoid toggling unintentionally
    // at least one field set
    if (p.allotment === undefined && p.closed === undefined && p.priceOverride === undefined) return null;
    return p;
  }, [start, end, allotment, closed, price]);

  const validateDates = (): boolean => {
    if (!start || !end) { setDateError('Please select a start and end date'); return false; }
    if (end.isBefore(start)) { setDateError('End date must be after or equal to start'); return false; }
    setDateError(null); return true;
  };

  const reset = () => { setAllotment(''); setClosed(false); setPrice(''); };

  const submitHotel = async () => {
    if (!validateDates() || !payload) { if (!payload) toastError('Set at least one field'); return; }
    try { setSubmitting(true); await applyHotelAvailability(hotelId, payload); toastSuccess('Availability updated'); reset(); }
    catch (e:any) { const msg = e?.response?.data?.error || 'Update failed'; if (msg.includes('Invalid date range')) setDateError(msg); else toastError(msg); }
    finally { setSubmitting(false); }
  };

  const submitRoomType = async () => {
    if (!selRoomTypeId) { toastError('Select a room type'); return; }
    if (!validateDates() || !payload) { if (!payload) toastError('Set at least one field'); return; }
    try { setSubmitting(true); await applyRoomTypeAvailability(Number(selRoomTypeId), payload); toastSuccess('Availability updated'); reset(); }
    catch (e:any) { const msg = e?.response?.data?.error || 'Update failed'; if (msg.includes('Invalid date range')) setDateError(msg); else toastError(msg); }
    finally { setSubmitting(false); }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>Bulk edit availability</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <TextField type="date" label="Start" InputLabelProps={{ shrink:true }} value={start?.format('YYYY-MM-DD')||''} onChange={(e)=> setStart(dayjs(e.target.value||undefined))} />
            <TextField type="date" label="End" InputLabelProps={{ shrink:true }} value={end?.format('YYYY-MM-DD')||''} onChange={(e)=> setEnd(dayjs(e.target.value||undefined))} />
            {dateError && <Typography color="error" variant="body2">{dateError}</Typography>}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <TextField type="number" label="Allotment" value={allotment} onChange={(e)=> setAllotment(e.target.value)} />
            <FormControlLabel control={<Checkbox checked={closed} onChange={(e)=> setClosed(e.target.checked)} />} label="Closed" />
            <TextField type="number" label="Price override" value={price} onChange={(e)=> setPrice(e.target.value)} />
          </Stack>
        </Grid>
      </Grid>

      <Stack direction={{ xs:'column', md:'row' }} spacing={2} mt={3} alignItems="center">
        <Button disabled={submitting} variant="contained" onClick={submitHotel}>Apply to hotel</Button>
        <TextField select SelectProps={{ native:true }} label="Room type" value={selRoomTypeId} onChange={(e)=> setSelRoomTypeId(e.target.value ? Number(e.target.value) : '')} sx={{ minWidth:240 }}>
          <option value=""></option>
          {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
        </TextField>
        <Button disabled={submitting} variant="outlined" onClick={submitRoomType}>Apply to selected room type</Button>
      </Stack>
    </Box>
  );
};

export default AvailabilityTab;


